import { useEffect, useRef } from "react";

import phoneExercises from "./assets/MobileApp/ScrollAnimation/1scrollONLYPHONE.png";
import phoneMood from "./assets/MobileApp/ScrollAnimation/2scrollONLYPHONE.png";
import phoneMemory from "./assets/MobileApp/ScrollAnimation/3scrollONLYPHONE.png";
import phoneVoice from "./assets/MobileApp/ScrollAnimation/4scrollONLYPHONE.png";

/* Four chapters of one conversation, handed over to each other by scroll.
   Only the *ONLYPHONE renders are used here — the matching Nscroll.png files
   are flattened design comps (gradient + copy + device in one picture) and
   would duplicate the headline and the phone if they were ever rendered.

   Each PNG is a 1024x1536 transparent canvas with the device floating somewhere
   inside it, and every render sits in its canvas slightly differently. `frame`
   is the device's own opaque bounds, read off the alpha channel, so the wrapper
   can be sized to the *body* instead of the canvas it floats in:
     wf  body width  / canvas width
     hf  body height / canvas height
     tf  body top edge, as a fraction of canvas height
     cx  body centre, as a fraction of canvas width
   Sizing on hf is the important one: it makes every chapter's device exactly
   the same height on screen, which is what lets one uniform clip schedule read
   identically through all three handovers. Widths then differ by ~10% between
   renders, which never shows — no two devices are ever fully visible at once. */
const CHAPTERS = [
  {
    id: "exercises",
    num: "01",
    headline: "Personalised exercises & counsellor recommendations",
    subtext:
      "Exercises and counsellor suggestions matched to how you are actually doing.",
    img: phoneExercises,
    alt: "Sentio chat recommending a self-compassion break and a motivation journal",
    frame: { wf: 0.725586, hf: 0.988932, tf: 0.00651, cx: 0.499512 },
  },
  {
    id: "mood",
    num: "02",
    headline: "Reads how you're feeling",
    subtext:
      "Responses shift in tone and depth as the conversation moves.",
    img: phoneMood,
    alt: "Sentio chat softening its tone and offering a one-minute grounding exercise",
    frame: { wf: 0.740234, hf: 0.991536, tf: 0.005208, cx: 0.499023 },
  },
  {
    id: "memory",
    num: "03",
    headline: "Remembers what matters to you",
    subtext:
      "Picks up where you left off, so you never start from scratch.",
    img: phoneMemory,
    alt: "Sentio chat recalling a detail from an earlier conversation",
    frame: { wf: 0.766602, hf: 0.988932, tf: 0.002604, cx: 0.500488 },
  },
  {
    id: "voice",
    num: "04",
    headline: "Talk, don't type",
    subtext:
      "Say it out loud. Sentio listens and replies, hands free.",
    img: phoneVoice,
    alt: "Sentio in voice mode, replying out loud",
    frame: { wf: 0.685547, hf: 0.979818, tf: 0.007161, cx: 0.5 },
  },
];

/* The scroll budget, as fractions of the wrapper's own travel. STEP is the
   cadence — how much scroll separates one chapter's handover from the next —
   and TRANS is how long a single handover lasts. TRANS is *twice* STEP, which
   is the whole point: a chapter starts exiting when it is only halfway through
   entering, so its enter and exit windows overlap and there is never a stretch
   of scroll where one card owns the stage alone. Two or three cards are on the
   stage for ~70% of the section.

   The 2:1 ratio is read off the reference, not guessed. In a frame where the
   middle card measured 18.3% clipped off its top and 12.8% off its bottom, the
   eased enter/exit values behind those insets are 0.72 and 0.216 — and their
   difference is exactly STEP/TRANS, so STEP/TRANS = 0.5.

   The last chapter finishes entering at (n-2)*STEP + TRANS, which with the 2:1
   ratio is n*STEP. TAIL is what is left over after that: a short run of scroll
   where chapter four sits alone and readable before the stage unsticks. Without
   it the section would release on the same pixel the last card arrived. */
const TAIL = 0.08;
const STEP = (1 - TAIL) / CHAPTERS.length;
const TRANS = STEP * 2;

/* How far the clip travels, in % of card height. The 4 *over* 100 is the gap.
   During a handover the outgoing card's bottom inset and the incoming card's
   top inset are driven by the same eased value e, so they always sum to CAP:
     outgoing visible: 0 .. (100 - CAP*e)
     incoming visible: CAP*(1 - e) .. 100
   which leaves CAP - 100 = 4% of plain page background between them, constant
   for the whole handover. Capping at exactly 100 would make the two slices meet
   edge to edge; anything under 100 would overlap them. Measured off the
   reference: a 34px gap on a 903px stage is 3.8%, so CAP is 104, not 106. */
const CAP = 104;

/* Copy can't just track its own chapter's window any more — with the windows
   overlapping, a chapter is entering and exiting at the same time, so a naive
   fade would start fading out before it had finished fading in. These are the
   ranges of `entering` and `exiting` the fade actually runs over, picked so the
   opaque stretch lands on the scroll where that chapter owns the largest of the
   visible slices. Tune by eye; they are not derived from anything. */
const FADE_IN = [0.40, 0.68];
const FADE_OUT = [0.32, 0.60];

const range = (v, [a, b]) => (v - a) / (b - a);

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (s) => s * s * (3 - 2 * s);

export default function ScrollChapters() {
  const wrapRef = useRef(null);
  const cardRefs = useRef([]);
  const copyRefs = useRef([]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    /* Stacked and reduced-motion layouts render as four plain cards — there is
       no sticky stage to scrub against, so the effect is skipped entirely
       rather than driven and then overridden.

       1068px is the page's tablet line, the width at which every other
       two-column layout collapses. This string has to stay byte-identical to
       the query guarding .ma-chapters-* in MobileAppStyles.js: if JS stops
       writing at a different width than CSS stops honouring it, there is a
       band of viewports where the stage is static but the clip is still being
       driven, and the cards vanish. */
    const still = window.matchMedia(
      "(max-width: 1068px), (prefers-reduced-motion: reduce)"
    );

    let frame = null;

    const reset = () => {
      cardRefs.current.forEach((node) => {
        if (!node) return;
        node.style.clipPath = "";
        node.style.visibility = "";
      });
      copyRefs.current.forEach((node) => {
        if (!node) return;
        node.style.opacity = "";
        node.style.transform = "";
      });
    };

    const apply = () => {
      frame = null;
      const travel = wrap.offsetHeight - window.innerHeight;
      if (travel <= 0) return;
      const p = clamp01(-wrap.getBoundingClientRect().top / travel);

      CHAPTERS.forEach((_, i) => {
        const card = cardRefs.current[i];
        const copy = copyRefs.current[i];
        if (!card) return;

        // A chapter enters on the handover its predecessor exits on — same
        // window, same easing — which is what holds the gap constant. The two
        // windows are STEP apart and TRANS long, and TRANS > STEP, so a chapter
        // is still entering while it has already begun to exit: that overlap is
        // what keeps a neighbour's slice on the stage at every scroll position.
        const enterStart = (i - 1) * STEP;
        const exitStart = i * STEP;
        const entering = i > 0 ? clamp01((p - enterStart) / TRANS) : 1;
        const exiting = i < CHAPTERS.length - 1 ? clamp01((p - exitStart) / TRANS) : 0;

        const top = CAP * (1 - smooth(entering));
        const bottom = CAP * smooth(exiting);

        card.style.clipPath = `inset(${top.toFixed(3)}% 0 ${bottom.toFixed(3)}% 0)`;
        card.style.visibility = top >= 100 || bottom >= 100 ? "hidden" : "";

        if (copy) {
          const fadeIn = clamp01(range(entering, FADE_IN));
          const fadeOut = 1 - clamp01(range(exiting, FADE_OUT));
          copy.style.opacity = Math.min(fadeIn, fadeOut).toFixed(3);
          copy.style.transform = `translate3d(0, ${((1 - fadeIn) * 16 - (1 - fadeOut) * 16).toFixed(1)}px, 0)`;
        }
      });
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(apply);
    };

    const sync = () => {
      if (still.matches) {
        window.removeEventListener("scroll", onScroll);
        reset();
      } else {
        window.addEventListener("scroll", onScroll, { passive: true });
        apply();
      }
    };

    sync();
    still.addEventListener("change", sync);
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      still.removeEventListener("change", sync);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="ma-chapters">
      <div className="ma-chapters-intro">
        <div className="ma-label">
          <span className="ma-label-num">03</span>
          <span className="ma-label-rule" />
          Companion
        </div>
        <h2 className="ma-h2 ma-h2-ink">
          Your thoughts deserve a listener.
        </h2>
        <p className="ma-chapters-sub">No judgement. No pressure. No appointment.</p>
        <p className="ma-chapters-quote">Here at 2 AM. Here on the good days too.</p>
      </div>

      {/* The wrapper only exists to buy scroll distance; everything is drawn in
          the sticky stage inside it, one absolutely-positioned layer per
          chapter, every one of them filling the stage edge to edge. Nothing
          moves or resizes — the layers are stacked in place and only their clip
          changes. Two cards are never drawn over the same pixels: the clip
          leaves the outgoing one at the top of the stage and the incoming one at
          the bottom, with the page's own background between them. */}
      <div className="ma-chapters-track" ref={wrapRef}>
        <div className="ma-chapters-stage">
          {CHAPTERS.map((chapter, i) => (
            <article
              key={chapter.id}
              className="ma-chapter"
              style={{ zIndex: i + 1 }}
              aria-label={chapter.headline}
            >
              <div className="ma-chapter-card" ref={(el) => { cardRefs.current[i] = el; }}>
                <div className="ma-chapter-copy" ref={(el) => { copyRefs.current[i] = el; }}>
                  <span className="ma-chapter-num">{chapter.num}</span>
                  <h3 className="ma-chapter-h">{chapter.headline}</h3>
                  <p className="ma-chapter-p">{chapter.subtext}</p>
                </div>

                {/* Sized to the device inside the PNG, not to the PNG. The image
                    is blown up by 1/hf and pulled up by its own top margin, so
                    the wrapper's box and the device's body are the same rect —
                    which means the clip percentages land where they look like
                    they land. */}
                <div
                  className="ma-chapter-device"
                  style={{
                    "--ma-ch-wf": chapter.frame.wf,
                    "--ma-ch-hf": chapter.frame.hf,
                    "--ma-ch-tf": chapter.frame.tf,
                    "--ma-ch-cx": chapter.frame.cx,
                  }}
                >
                  <img
                    src={chapter.img}
                    alt={chapter.alt}
                    width={1024}
                    height={1536}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
